import type {
  EventIdPublic,
  EventParticipantIdPublic,
  MatchingPipelineRunIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";

type EventHooks = FrontendDAL["events"];
type FeatureConfigHooks = FrontendDAL["featureConfig"];
type MatchingEngineHooks = FrontendDAL["matchingEngineAdmin"];
type EventParticipantsAdminHooks = FrontendDAL["eventParticipantsAdmin"];
type ParticipantInspectorAdminHooks = FrontendDAL["participantInspectorAdmin"];
type ParticipantHooks = FrontendDAL["participants"];
type DashboardAdminHooks = FrontendDAL["dashboardAdmin"];

export interface EventAdminActionsInput {
  readonly eventIdPublic: EventIdPublic;
}

export interface EventAdminBundle {
  readonly updateEventInternal: ReturnType<
    EventHooks["useUpdateEventInternal"]
  >;
  readonly applyEventPreset: ReturnType<
    FeatureConfigHooks["useApplyEventPreset"]
  >;
  readonly triggerPipeline: ReturnType<
    MatchingEngineHooks["useTriggerPipeline"]
  >;
  readonly cancelRun: ReturnType<MatchingEngineHooks["useCancelRun"]>;
  readonly configureCrm: ReturnType<EventHooks["useConfigureCrm"]>;
  readonly seedDemoParticipants: ReturnType<
    ParticipantHooks["useSeedDemoParticipants"]
  >;
}

export interface EventAdminQueries {
  readonly useGetEventAdminView: () => ReturnType<
    EventHooks["useGetEventAdminView"]
  >;
  readonly useGetRunSnapshot: (
    runIdPublic?: MatchingPipelineRunIdPublic,
  ) => ReturnType<MatchingEngineHooks["useGetRunSnapshot"]>;
  readonly useGetParticipantSnapshot: (
    runIdPublic: MatchingPipelineRunIdPublic,
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<MatchingEngineHooks["useGetParticipantSnapshot"]>;
  /**
   * The run's cohort step results — the documents that name no person, which the
   * run snapshot and the participant snapshot both leave out by design.
   */
  readonly useGetCohortSnapshot: (
    runIdPublic: MatchingPipelineRunIdPublic,
  ) => ReturnType<MatchingEngineHooks["useGetCohortSnapshot"]>;
  /**
   * The event roster, served by the admin event-participants endpoint. Shared
   * event-admin surface — not matching-engine scoped — so any admin view that
   * needs a participant list reads it from here.
   */
  readonly useGetEventParticipants: () => ReturnType<
    EventParticipantsAdminHooks["useGetEventParticipants"]
  >;
  readonly useGetParticipantAssumptions: (
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<
    ParticipantInspectorAdminHooks["useGetParticipantAssumptions"]
  >;
  readonly useRegenerateParticipantAssumptions: (
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<
    ParticipantInspectorAdminHooks["useRegenerateParticipantAssumptions"]
  >;
  readonly useGetParticipantReasonsToMeet: (
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<
    ParticipantInspectorAdminHooks["useGetParticipantReasonsToMeet"]
  >;
  readonly useRecomputeParticipantReasonsToMeet: (
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<
    ParticipantInspectorAdminHooks["useRecomputeParticipantReasonsToMeet"]
  >;
  readonly useRecomputeEventReasonsToMeet: () => ReturnType<
    ParticipantInspectorAdminHooks["useRecomputeEventReasonsToMeet"]
  >;
  readonly useRegenerateDirectoryEmbeddings: () => ReturnType<
    ParticipantInspectorAdminHooks["useRegenerateDirectoryEmbeddings"]
  >;
  readonly useGetParticipantEnrichment: (
    participantIdPublic: EventParticipantIdPublic,
  ) => ReturnType<
    ParticipantInspectorAdminHooks["useGetParticipantEnrichment"]
  >;
  readonly useGetCompositionDescriptors: () => ReturnType<
    MatchingEngineHooks["useGetCompositionDescriptors"]
  >;
  readonly useGetParticipants: () => ReturnType<
    ParticipantHooks["useGetParticipants"]
  >;
  readonly useGetEventDashboard: () => ReturnType<
    DashboardAdminHooks["useGetEventDashboard"]
  >;
}

export interface EventAdminActions extends EventAdminQueries {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly admin: EventAdminBundle | null;
}

/**
 * Event-scoped admin actions — mirrors the backend `AdminActions.forEvent()` /
 * `AdminEventActions`. Always returns a non-null result object; the inner `admin`
 * mutation bundle is `null` while the current-user query is pending or when the
 * caller is not an admin (`user:admin`; admins may access any event) — a non-null
 * `admin` IS the authorization. Standalone (not exposed off `useUserActions`) so
 * non-admin apps never instantiate the admin mutation hooks. The event-bound query
 * sub-hooks close over `eventIdPublic`; the matching-engine DAL query hooks take no
 * `enabled`, so they rely on the consumer opting in. Enrichment ops are global
 * admin (not event-scoped) and live on `useAdminActions`.
 *
 * Deferred (backend `AdminEventActions` ops without a DAL hook):
 * `deleteConnectionRequest(s)`, event-scoped `broadcastHandoff`, and
 * `anonymizeParticipants` — add DAL hooks when a consumer needs them.
 */
export function useEventAdminActions(
  input: EventAdminActionsInput,
): EventAdminActions {
  const { eventIdPublic } = input;
  const {
    isLoading,
    error,
    subject: _admin,
    bundle,
    ...queries
  } = useGatedAction({
    useDeps: () => ({
      currentUser: dal().auth.useCurrentUser(),
      updateEventInternal: dal().events.useUpdateEventInternal(),
      applyEventPreset: dal().featureConfig.useApplyEventPreset(),
      triggerPipeline: dal().matchingEngineAdmin.useTriggerPipeline(),
      cancelRun: dal().matchingEngineAdmin.useCancelRun(),
      configureCrm: dal().events.useConfigureCrm(),
      seedDemoParticipants: dal().participants.useSeedDemoParticipants(),
    }),
    isLoading: (d) => d.currentUser.isLoading,
    error: (d) =>
      d.currentUser.error instanceof Error ? d.currentUser.error : null,
    authorize: (d) => {
      const user = d.currentUser.data;
      if (user === null || user === undefined) return null;
      return user.$roles.includes("user:admin") ? user : null;
    },
    bundle: (d) => ({
      updateEventInternal: d.updateEventInternal,
      applyEventPreset: d.applyEventPreset,
      triggerPipeline: d.triggerPipeline,
      cancelRun: d.cancelRun,
      configureCrm: d.configureCrm,
      seedDemoParticipants: d.seedDemoParticipants,
    }),
    extra: (_d, { isAuthorized: isAdmin }): EventAdminQueries => ({
      useGetEventAdminView: () =>
        dal().events.useGetEventAdminView({
          eventIdPublicOrSlug: eventIdPublic,
          enabled: isAdmin,
        }),
      useGetRunSnapshot: (runIdPublic) =>
        dal().matchingEngineAdmin.useGetRunSnapshot(eventIdPublic, runIdPublic),
      useGetParticipantSnapshot: (runIdPublic, participantIdPublic) =>
        dal().matchingEngineAdmin.useGetParticipantSnapshot(
          eventIdPublic,
          runIdPublic,
          participantIdPublic,
        ),
      useGetCohortSnapshot: (runIdPublic) =>
        dal().matchingEngineAdmin.useGetCohortSnapshot(
          eventIdPublic,
          runIdPublic,
        ),
      useGetEventParticipants: () =>
        dal().eventParticipantsAdmin.useGetEventParticipants(eventIdPublic),
      useGetParticipantAssumptions: (participantIdPublic) =>
        dal().participantInspectorAdmin.useGetParticipantAssumptions(
          eventIdPublic,
          participantIdPublic,
        ),
      useRegenerateParticipantAssumptions: (participantIdPublic) =>
        dal().participantInspectorAdmin.useRegenerateParticipantAssumptions(
          eventIdPublic,
          participantIdPublic,
        ),
      useGetParticipantReasonsToMeet: (participantIdPublic) =>
        dal().participantInspectorAdmin.useGetParticipantReasonsToMeet(
          eventIdPublic,
          participantIdPublic,
        ),
      useRecomputeParticipantReasonsToMeet: (participantIdPublic) =>
        dal().participantInspectorAdmin.useRecomputeParticipantReasonsToMeet(
          eventIdPublic,
          participantIdPublic,
        ),
      useRecomputeEventReasonsToMeet: () =>
        dal().participantInspectorAdmin.useRecomputeEventReasonsToMeet(
          eventIdPublic,
        ),
      useRegenerateDirectoryEmbeddings: () =>
        dal().participantInspectorAdmin.useRegenerateDirectoryEmbeddings(
          eventIdPublic,
        ),
      useGetParticipantEnrichment: (participantIdPublic) =>
        dal().participantInspectorAdmin.useGetParticipantEnrichment(
          eventIdPublic,
          participantIdPublic,
        ),
      useGetCompositionDescriptors: () =>
        dal().matchingEngineAdmin.useGetCompositionDescriptors(eventIdPublic),
      useGetParticipants: () =>
        dal().participants.useGetParticipants(
          { eventIdPublic },
          { enabled: isAdmin },
        ),
      useGetEventDashboard: () =>
        dal().dashboardAdmin.useGetEventDashboard(eventIdPublic, {
          enabled: isAdmin,
        }),
    }),
  });

  return { isLoading, error, admin: bundle, ...queries };
}
